import { Edit3, Save, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { readFile } from "../lib/agent-api";
import { cn } from "../lib/utils";

interface FileEditorProps {
	path: string;
	onSave?: (path: string, content: string) => Promise<void>;
	onClose?: () => void;
}

export function FileEditor({ path, onSave, onClose }: FileEditorProps) {
	const [content, setContent] = useState("");
	const [original, setOriginal] = useState("");
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setLoading(true);
		setError(null);
		setEditing(false);
		readFile(path)
			.then((res) => {
				const text = res.content || "";
				setContent(text);
				setOriginal(text);
			})
			.catch((e) => setError(e instanceof Error ? e.message : "Failed to read file"))
			.finally(() => setLoading(false));
	}, [path]);

	const handleSave = useCallback(async () => {
		if (!onSave) return;
		setSaving(true);
		try {
			await onSave(path, content);
			setOriginal(content);
			setEditing(false);
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to save");
		} finally {
			setSaving(false);
		}
	}, [path, content, onSave]);

	const hasChanges = content !== original;

	if (loading) {
		return (
			<div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
				Loading...
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center h-40 text-sm text-destructive">{error}</div>
		);
	}

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center justify-between px-3 py-2 border-b border-border bg-secondary/30">
				<div className="flex items-center gap-2 min-w-0">
					<span className="text-xs font-mono text-muted-foreground truncate">{path}</span>
					{hasChanges && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
				</div>
				<div className="flex items-center gap-1 shrink-0">
					{onSave && (
						<button
							type="button"
							onClick={() => (editing ? handleSave() : setEditing(true))}
							disabled={saving}
							className={cn(
								"p-1.5 rounded-md transition-colors text-xs flex items-center gap-1",
								editing
									? "bg-primary text-primary-foreground hover:bg-primary/90"
									: "hover:bg-secondary text-muted-foreground"
							)}
						>
							{editing ? (
								<>
									<Save className="w-3 h-3" />
									{saving ? "Saving..." : "Save"}
								</>
							) : (
								<>
									<Edit3 className="w-3 h-3" />
									Edit
								</>
							)}
						</button>
					)}
					{editing && (
						<button
							type="button"
							onClick={() => {
								setContent(original);
								setEditing(false);
							}}
							className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground transition-colors"
						>
							<X className="w-3 h-3" />
						</button>
					)}
					{onClose && (
						<button
							type="button"
							onClick={onClose}
							className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground transition-colors"
						>
							<X className="w-3 h-3" />
						</button>
					)}
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-auto">
				{editing ? (
					<textarea
						value={content}
						onChange={(e) => setContent(e.target.value)}
						className="w-full h-full min-h-[200px] p-3 bg-transparent text-xs font-mono resize-none outline-none"
						spellCheck={false}
					/>
				) : (
					<pre className="p-3 text-xs font-mono whitespace-pre-wrap break-all">
						<code>{content}</code>
					</pre>
				)}
			</div>
		</div>
	);
}
